import { useId } from 'react';
import type { ChangeEvent, JSX, SelectHTMLAttributes } from 'react';
import { useFieldAria } from './Field';
import { CONTROL_HEIGHT, FOCUS_RING, INPUT_BASE, cx } from './styles';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: readonly SelectOption[];
}

export interface SelectProps
  extends Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    | 'aria-describedby'
    | 'children'
    | 'className'
    | 'disabled'
    | 'id'
    | 'onChange'
    | 'required'
    | 'size'
    | 'value'
  > {
  value: string;
  onChange: (value: string) => void;
  /** 平铺选项；与 `groups` 二选一（分组场景用 `groups`） */
  options?: readonly SelectOption[];
  groups?: readonly SelectGroup[];
  /**
   * 空选项的文案，例如"未选择"。它**保持可选**：在地面站里"还没读到 / 未指定"
   * 是一种合法状态，把它设成 disabled 用户就再也回不到这个状态了。
   */
  placeholder?: string;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  id?: string;
  'aria-describedby'?: string;
}

/**
 * 下拉选择。
 *
 * 刻意用**原生 `<select>`**，不做自定义弹出层：
 *   - 键盘操作、读屏、移动端滚轮选择、超长列表的滚动都是浏览器给的，不写一行代码；
 *   - 弹出层由操作系统绘制，因此不会和页面的 z-index 阶梯打架 ——
 *     GCS 有一条硬约束"任何东西都不许遮住遥测"，自己实现浮层的选择器是这条约束的
 *     主要破坏者（一个 z-index 没排好，弹出的长列表就盖住了姿态仪）；
 *   - 代价：弹出层的样式无法完全跟随主题（受操作系统影响）。这是**刻意接受**的取舍。
 *
 * 弹层定位属于浮层系统的职责范围，本组件不碰。
 */
export function Select({
  value,
  onChange,
  options,
  groups,
  placeholder,
  invalid = false,
  required = false,
  disabled = false,
  size = 'md',
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: SelectProps): JSX.Element {
  const field = useFieldAria();
  const generatedId = useId().replaceAll(':', '');
  const controlId = field === null ? (id ?? `select-${generatedId}`) : field.controlId;
  const isRequired = required || field?.required === true;
  const isDisabled = disabled || field?.disabled === true;
  const isInvalid = invalid || field?.invalid === true;
  const describedBy = cx(field?.describedBy, ariaDescribedBy);

  function handleChange(event: ChangeEvent<HTMLSelectElement>): void {
    onChange(event.target.value);
  }

  return (
    <select
      {...rest}
      id={controlId}
      value={value}
      onChange={handleChange}
      required={isRequired ? true : undefined}
      disabled={isDisabled}
      aria-invalid={isInvalid ? true : undefined}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      className={cx(INPUT_BASE, CONTROL_HEIGHT[size], 'px-2 py-1', FOCUS_RING, className)}
    >
      {placeholder === undefined ? null : <option value="">{placeholder}</option>}
      {options?.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled === true}>
          {option.label}
        </option>
      ))}
      {groups?.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled === true}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
